import { fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { useCallback, useMemo } from 'react';

export function useBatchEditorApi() {
  const fetchApi = useApi(fetchApiRef);

  const requestJson = useCallback(
    async <T>(
      url: string,
      init?: RequestInit,
      options?: { includeCredentials?: boolean },
    ): Promise<T> => {
      const response = await fetchApi.fetch(url, {
        ...init,
        credentials: options?.includeCredentials ? 'include' : init?.credentials,
      });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || `${response.status} ${response.statusText}`);
      }
      return (await response.json()) as T;
    },
    [fetchApi],
  );

  const getJson = useCallback(
    <T>(url: string, options?: { includeCredentials?: boolean }) =>
      requestJson<T>(url, undefined, options),
    [requestJson],
  );

  const postJson = useCallback(
    <T>(url: string, body: unknown, init?: RequestInit) =>
      requestJson<T>(url, {
        ...init,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(init?.headers ?? {}),
        },
        body: JSON.stringify(body),
      }),
    [requestJson],
  );

  return useMemo(
    () => ({
      getJson,
      postJson,
      requestJson,
    }),
    [getJson, postJson, requestJson],
  );
}
